import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { OverviewPage } from './pages/OverviewPage';
import { LiveScansPage } from './pages/LiveScansPage';
import { HistoryPage } from './pages/HistoryPage';
import { GeneratePage } from './pages/GeneratePage';
import { SettingsPage } from './pages/SettingsPage';
import { StatsPage } from './pages/StatsPage';
import { LoginPage } from './pages/LoginPage';
import { useAuth } from './hooks/useAuth';
import { isVideoEnabled } from './utils/api';
import { VideoLiveScansPage } from './video/pages/LiveScansPage';
import { VideoHistoryPage } from './video/pages/HistoryPage';
import { VideoSettingsPage } from './video/pages/SettingsPage';
import { VideoOverviewPage } from './video/pages/OverviewPage';
import { VideoStatsPage } from './video/pages/StatsPage';

/**
 * Tiny helper component used by the legacy redirects. `useLocation` lets
 * us preserve the query string when bouncing `/foo?bar=1` to
 * `/lightning/foo?bar=1`, which matters for any external bookmark / link
 * still pointing at the pre-split URLs.
 */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/**
 * Top-level routing for the unified SPA.
 *
 * The shell hosts two mini-apps that are always deployed side-by-side:
 *   - `/lightning/*`  — the original lightning UI (default app)
 *   - `/video/*`      — the 360-video UI
 *
 * The `videoEnabled` runtime check below is a dev-mode safety net for
 * developers running `make web-dev` without setting
 * `VITE_VIDEO_API_BASE` in their `.env` — in that case the video
 * routes stay unmounted to avoid surfacing a tab that would just 502.
 * Every official build (CI + `make deploy`) sets the env var, so the
 * routes are always present in real deployments.
 *
 * Every old top-level path (`/overview`, `/live`, `/history`, …) is
 * preserved as a permanent redirect to its `/lightning/*` equivalent so
 * existing bookmarks, scripts, and external integrations don't break.
 */
function App() {
  const { isAuthenticated } = useAuth();
  const videoEnabled = isVideoEnabled();

  return (
    <BrowserRouter>
      {!isAuthenticated ? (
        <LoginPage />
      ) : (
        <AppShell>
          <Routes>
            {/* ---- Lightning routes (default app) ---- */}
            <Route path="/lightning/overview" element={<OverviewPage />} />
            <Route path="/lightning/live" element={<LiveScansPage />} />
            <Route path="/lightning/history" element={<HistoryPage />} />
            <Route path="/lightning/generate" element={<GeneratePage />} />
            <Route path="/lightning/settings" element={<SettingsPage />} />
            <Route path="/lightning/stats" element={<StatsPage />} />
            <Route path="/lightning" element={<Navigate to="/lightning/overview" replace />} />
            <Route path="/lightning/monitor" element={<Navigate to="/lightning/live" replace />} />

            {/* ---- Video routes (always present in CI/`make deploy` builds) ---- */}
            {videoEnabled && (
              <>
                <Route path="/video/overview" element={<VideoOverviewPage />} />
                <Route path="/video/live" element={<VideoLiveScansPage refreshKey={0} />} />
                <Route path="/video/history" element={<VideoHistoryPage />} />
                <Route path="/video/stats" element={<VideoStatsPage />} />
                <Route path="/video/settings" element={<VideoSettingsPage />} />
                <Route path="/video" element={<Navigate to="/video/overview" replace />} />
              </>
            )}

            {/* ---- Legacy redirects (preserve query/hash) ---- */}
            <Route path="/overview" element={<LegacyRedirect to="/lightning/overview" />} />
            <Route path="/live" element={<LegacyRedirect to="/lightning/live" />} />
            <Route path="/history" element={<LegacyRedirect to="/lightning/history" />} />
            <Route path="/generate" element={<LegacyRedirect to="/lightning/generate" />} />
            <Route path="/settings" element={<LegacyRedirect to="/lightning/settings" />} />
            <Route path="/stats" element={<LegacyRedirect to="/lightning/stats" />} />
            <Route path="/monitor" element={<LegacyRedirect to="/lightning/live" />} />

            {/* Default + 404: send everything to the lightning overview. */}
            <Route path="/" element={<Navigate to="/lightning/overview" replace />} />
            <Route path="*" element={<Navigate to="/lightning/overview" replace />} />
          </Routes>
        </AppShell>
      )}
    </BrowserRouter>
  );
}

export default App;
